import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { toolBySlug, COLOR } from "../lib/tools";
import { CALCULATORS } from "../components/tools/calculators";
import { useToolsT } from "../lib/toolsI18n";
import { SITE } from "../lib/seo";

// Minimal, chrome-free calculator page meant to be embedded in an <iframe> on other sites.
// Includes a required "Powered by AskMyDoctor AI" backlink and posts its height to the parent
// so host pages using our snippet can auto-fit the iframe (no inner scrollbar).
export default function EmbedToolPage() {
  const { slug } = useParams();
  const { tt, reg } = useToolsT();
  const tool = toolBySlug(slug);
  const Calc = CALCULATORS[slug];
  const toolUrl = `${SITE}/tools/${slug}`;

  useEffect(() => {
    const post = () => {
      const h = Math.ceil(document.documentElement.scrollHeight);
      try {
        window.parent.postMessage({ type: "amd-embed-height", slug, height: h }, "*");
      } catch {
        /* cross-origin — ignore */
      }
    };
    post();
    const ro = new ResizeObserver(post);
    ro.observe(document.body);
    window.addEventListener("load", post);
    return () => {
      ro.disconnect();
      window.removeEventListener("load", post);
    };
  }, [slug]);

  if (!tool || !Calc) {
    return (
      <div className="min-h-screen grid place-items-center bg-white p-6 text-center">
        <a href={`${SITE}/tools`} className="text-teal-700 font-bold">AskMyDoctor AI — Free Tools</a>
      </div>
    );
  }

  const c = COLOR[tool.color];
  const Icon = tool.icon;
  const meta = reg(slug);

  return (
    <div className="min-h-screen bg-white p-4 sm:p-5 flex flex-col" data-testid={`embed-tool-${slug}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`inline-grid place-items-center w-10 h-10 rounded-xl bg-gradient-to-br ${c.grad} text-white shadow-sm`}>
          <Icon className="w-5 h-5" />
        </div>
        <h1 className="font-heading font-bold text-lg text-slate-900 leading-tight">{meta.t}</h1>
      </div>

      <div className="flex-1">
        <Calc />
      </div>

      <p className="mt-4 text-xs text-slate-400 leading-relaxed border-t border-slate-100 pt-3">{tt("toolDisclaimer")}</p>
      <a
        href={toolUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="embed-powered"
        className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-teal-700 hover:text-teal-800"
      >
        {tt("poweredBy")}
      </a>
    </div>
  );
}
